import { ArrowDownIcon, ArrowUpIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { memo, useCallback, useState } from 'react';
import { Button } from '../../components/Button';
import { Input } from '../../components/inputs/Input';
import { Modal } from '../../components/Modal';
import { Tooltip } from '../../components/Tooltip';
import { T, useT } from '../../translation/src';

export interface EditableCategory {
  id: string;
  label: string;
}

interface CategoriesModalProps {
  open: boolean;
  onClose: () => void;
  categories: readonly EditableCategory[];
  /** Categories that still hold objects can't be removed. */
  isInUse: (id: string) => boolean;
  onSave: (categories: EditableCategory[]) => Promise<void>;
}

const newCategoryId = (): string => `cat_${Math.random().toString(36).slice(2, 10)}`;

/** The organisation's own library categories: add, rename, reorder, remove (when empty). */
const CategoriesModal_: React.FC<CategoriesModalProps> = ({ open, onClose, categories, isInUse, onSave }) => {
  // Seeded on mount: the page remounts the modal each time it opens (key), so edits never outlive a cancel.
  const [draft, setDraft] = useState<EditableCategory[]>(() => categories.map((c) => ({ ...c })));
  const [saving, setSaving] = useState(false);
  const placeholder = useT('category name', { swc: true });
  const inUseMessage = useT('move its objects to another category first', { swc: true });
  const moveUp = useT('move up', { swc: true });
  const moveDown = useT('move down', { swc: true });
  const remove = useT('remove', { swc: true });

  const update = useCallback((index: number, label: string) => setDraft((list) => list.map((c, i) => (i === index ? { ...c, label } : c))), []);
  const move = useCallback(
    (index: number, delta: -1 | 1) =>
      setDraft((list) => {
        const target = index + delta;
        if (target < 0 || target >= list.length) return list;
        const next = [...list];
        [next[index], next[target]] = [next[target], next[index]];
        return next;
      }),
    [],
  );
  const valid = draft.every((c) => c.label.trim().length > 0 && c.label.trim().length <= 60);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(draft.map((c) => ({ id: c.id, label: c.label.trim() })));
      onClose();
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, onClose]);

  return (
    <Modal.Container open={open} onClose={onClose} panelClassName="max-w-lg">
      <Modal.Header onClose={onClose}>
        <T _str="categories" swc />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-2">
        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
          <T _str="your own categories are listed after the built-in ones in the object library" swc />
        </p>
        {draft.length === 0 && (
          <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
            <T _str="no categories yet" swc />
          </p>
        )}
        {draft.map((category, index) => {
          const inUse = isInUse(category.id);
          return (
            <div key={category.id} className="flex items-center gap-2">
              <Input value={category.label} onChange={(e) => update(index, e.target.value)} placeholder={placeholder} maxLength={60} className="h-9 flex-1" />
              <button type="button" aria-label={moveUp} title={moveUp} disabled={index === 0} onClick={() => move(index, -1)} className="p-1 disabled:opacity-30">
                <ArrowUpIcon className="h-4 w-4" />
              </button>
              <button type="button" aria-label={moveDown} title={moveDown} disabled={index === draft.length - 1} onClick={() => move(index, 1)} className="p-1 disabled:opacity-30">
                <ArrowDownIcon className="h-4 w-4" />
              </button>
              {inUse ? (
                <Tooltip message={<span>{inUseMessage}</span>}>
                  <span className="block p-1 opacity-30">
                    <TrashIcon className="h-4 w-4" />
                  </span>
                </Tooltip>
              ) : (
                <button type="button" aria-label={remove} title={remove} onClick={() => setDraft((list) => list.filter((_, i) => i !== index))} className="p-1 hover:text-error-red-500">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          );
        })}
        <button type="button" onClick={() => setDraft((list) => [...list, { id: newCategoryId(), label: '' }])} className="mt-2 flex items-center gap-1 self-start text-sm text-french-violet-600 hover:underline dark:text-neon-green-300">
          <PlusIcon className="h-4 w-4" />
          <T _str="add category" swc />
        </button>
      </Modal.Body>
      <Modal.Footer className="justify-end gap-2">
        <Button variant="secondary" size="xs" onClick={onClose}>
          <T _str="cancel" swc />
        </Button>
        <Button variant="primary" size="xs" onClick={save} loading={saving} disabled={!valid}>
          <T _str="save" swc />
        </Button>
      </Modal.Footer>
    </Modal.Container>
  );
};

export const CategoriesModal = memo(CategoriesModal_);
